import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Dot from 'components/Dot';
import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import MobileActionSheet from 'components/MobileActionSheet';
import {
  ITEM_AVAILABILITY_STATUS,
  ITEM_AVAILABILITY_STATUS_MAPPING,
  ITEM_AVAILABILITY_STATUS_OPTIONS,
} from '../SkuItems.constants';
import { skuItemAvailabilityStatusMessages, itemsMessages } from '../SkuItems.messages';
import { messages as itemFormMessages } from './ItemForm.messages';

import './SkuItemAvailabilityStatus.scss';

const { block, element } = bem('sku-item-availability-status');

export const StatusMarker = ({ className, status }) => {
  if (status === ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY) {
    return <Isvg className={className} src={ICONS.noSymbol} />;
  }
  return <Dot className={className} color={ITEM_AVAILABILITY_STATUS_MAPPING[status]?.color} largeSize />;
};

StatusMarker.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf(ITEM_AVAILABILITY_STATUS_OPTIONS),
};

function SkuItemAvailabilityStatus({
  status,
  statusOptions,
  isPending,
  onChange,
  onMobileToggleClick,
  showActionsInMobile,
  onEditItem,
  onDeleteItem,
}) {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const resolvedStatus = status || ITEM_AVAILABILITY_STATUS.AVAILABLE;
  const isReadOnly = !onChange;
  const isDisabled = isPending || isReadOnly;

  if (isMobile) {
    const sheetStatusOptions = statusOptions.map(option => ({
      label: (
        <>
          <StatusMarker className={element('dropdown-status-marker')} status={option} />
          {intl.formatMessage(skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[option]?.message])}
        </>
      ),
      onClick: () => {
        if (option !== resolvedStatus && onChange) {
          onChange(option);
        }
      },
    }));

    const visibleStatusOptions = isPending ? [] : sheetStatusOptions;

    if (showActionsInMobile && onEditItem) {
      visibleStatusOptions.push({
        icon: ICONS.editPencilSku,
        label: intl.formatMessage(itemsMessages.editItem),
        onClick: onEditItem,
      });
    }

    const deleteOptions = [];
    if (showActionsInMobile && onDeleteItem) {
      deleteOptions.push({
        icon: ICONS.trashCanRed,
        label: intl.formatMessage(itemFormMessages.deleteButton),
        onClick: onDeleteItem,
        danger: true,
      });
    }

    let options;
    if (deleteOptions.length > 0 && visibleStatusOptions.length > 0) {
      options = [visibleStatusOptions, deleteOptions];
    } else if (deleteOptions.length > 0) {
      options = deleteOptions;
    } else {
      options = visibleStatusOptions;
    }

    const hasSheetContent = !isPending || showActionsInMobile;
    const isMobileDisabled = !hasSheetContent || (isReadOnly && !showActionsInMobile);

    return (
      <div className={block()} onClick={event => event.stopPropagation()} role="presentation">
        <button
          type="button"
          className={element('toggle', { pending: isPending, 'read-only': isReadOnly })}
          data-testid="sku-availability-toggle"
          onClick={onMobileToggleClick || (() => setIsSheetOpen(true))}
          disabled={isMobileDisabled}>
          <span className={element('marker')} data-testid="sku-availability-marker">
            {isPending ? <Isvg src={ICONS.clock} /> : <StatusMarker status={resolvedStatus} />}
          </span>
          <span className={element('label')}>
            {isPending ? (
              <Message message={skuItemAvailabilityStatusMessages.itemStatusPending} />
            ) : (
              <Message
                message={skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[resolvedStatus]?.message]}
              />
            )}
          </span>
          {!isMobileDisabled && (
            <span className={element('caret')} data-testid="sku-availability-caret">
              <SvgArrow direction="down" />
            </span>
          )}
        </button>
        {!onMobileToggleClick && (
          <MobileActionSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} options={options} />
        )}
      </div>
    );
  }

  return (
    <div className={block()} onClick={event => event.stopPropagation()} role="presentation">
      <Dropdown contentClassName={element('dropdown-menu')} isAlignTopToggleStyle attachment="right">
        {({ toggleMenu, closeMenu }) => (
          <>
            <button
              type="button"
              className={element('toggle', { pending: isPending, 'read-only': isReadOnly })}
              data-testid="sku-availability-toggle"
              onClick={toggleMenu}
              disabled={isDisabled}>
              <span className={element('marker')} data-testid="sku-availability-marker">
                {isPending ? <Isvg src={ICONS.clock} /> : <StatusMarker status={resolvedStatus} />}
              </span>
              <span className={element('label')}>
                {isPending ? (
                  <Message message={skuItemAvailabilityStatusMessages.itemStatusPending} />
                ) : (
                  <Message
                    message={
                      skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[resolvedStatus]?.message]
                    }
                  />
                )}
              </span>
              {!isDisabled && (
                <span className={element('caret')} data-testid="sku-availability-caret">
                  <SvgArrow direction="down" />
                </span>
              )}
            </button>
            <DropdownMenu>
              {!isPending &&
                statusOptions.map(option => (
                  <DropdownItem
                    key={option}
                    isSelected={resolvedStatus === option}
                    onClick={() => {
                      if (option !== resolvedStatus && onChange) {
                        onChange(option);
                      }
                      closeMenu();
                    }}>
                    <StatusMarker className={element('dropdown-status-marker')} status={option} />
                    <Message
                      message={skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[option]?.message]}
                    />
                  </DropdownItem>
                ))}
              {showActionsInMobile && (
                <>
                  <div className={element('divider')} />
                  {onEditItem && (
                    <DropdownItem
                      onClick={() => {
                        closeMenu();
                        onEditItem();
                      }}>
                      <span className={element('icon-wrapper')}>
                        <Isvg src={ICONS.editPencilSku} />
                      </span>
                      <Message message={itemsMessages.editItem} />
                    </DropdownItem>
                  )}
                  {onDeleteItem && (
                    <DropdownItem
                      onClick={() => {
                        closeMenu();
                        onDeleteItem();
                      }}>
                      <span className={element('icon-wrapper')}>
                        <Isvg src={ICONS.trashCanBlack} />
                      </span>
                      <Message message={itemFormMessages.deleteButton} />
                    </DropdownItem>
                  )}
                </>
              )}
            </DropdownMenu>
          </>
        )}
      </Dropdown>
    </div>
  );
}

SkuItemAvailabilityStatus.propTypes = {
  status: PropTypes.oneOf([...ITEM_AVAILABILITY_STATUS_OPTIONS, null, undefined]),
  statusOptions: PropTypes.arrayOf(PropTypes.oneOf(ITEM_AVAILABILITY_STATUS_OPTIONS)),
  isPending: PropTypes.bool,
  onChange: PropTypes.func,
  onMobileToggleClick: PropTypes.func,
  showActionsInMobile: PropTypes.bool,
  onEditItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
};

SkuItemAvailabilityStatus.defaultProps = {
  status: null,
  statusOptions: ITEM_AVAILABILITY_STATUS_OPTIONS,
  isPending: false,
  onChange: null,
  onMobileToggleClick: null,
  showActionsInMobile: false,
  onEditItem: null,
  onDeleteItem: null,
};

export default SkuItemAvailabilityStatus;
